import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function HowWeWorkPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How We Work With Farmers",
    "description": "From first conversation to completed project, here's exactly what to expect when you partner with Greenfield",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Initial Conversation",
        "text": "Understanding your operation through questions and listening"
      },
      {
        "@type": "HowToStep",
        "name": "Field Assessment",
        "text": "Visit your property to understand terrain and identify challenges"
      },
      {
        "@type": "HowToStep",
        "name": "Custom Plan",
        "text": "Detailed plan covering scope, timing, products, and transparent pricing"
      },
      {
        "@type": "HowToStep",
        "name": "Scheduling",
        "text": "Coordinate with your schedule and monitor weather conditions"
      },
      {
        "@type": "HowToStep",
        "name": "Execution",
        "text": "Certified pilots execute the plan with precision and safety"
      },
      {
        "@type": "HowToStep",
        "name": "Documentation",
        "text": "Receive detailed flight logs, coverage reports, and imagery"
      },
      {
        "@type": "HowToStep",
        "name": "Follow-Up",
        "text": "Check-in to ensure satisfaction and discuss ongoing partnership"
      }
    ]
  };

  const processSteps = [
    {
      number: '01',
      title: 'Initial Conversation',
      subtitle: 'Understanding Your Operation',
      description: 'It starts with a phone call or a cup of coffee. We want to hear about your land, your challenges, and your goals. No sales pitch—just questions and listening.',
      details: [
        'What crops are you running?',
        'What problems keep you up at night?',
        'What have you tried before?',
        'What does success look like for you?',
      ],
      icon: 'ri-chat-3-line',
      image: 'https://public.readdy.ai/ai/img_res/2edbc53d2a8c58cc332ad7e31ba84dcd.jpg',
    },
    {
      number: '02',
      title: 'Field Assessment',
      subtitle: 'Eyes on Your Land',
      description: 'We visit your property to understand the terrain, identify challenges, and assess what\'s needed. This isn\'t a formality—it\'s how we build a plan that actually works.',
      details: [
        'Walk the fields with you',
        'Identify problem areas and access points',
        'Assess terrain and obstacles',
        'Document current conditions',
      ],
      icon: 'ri-map-pin-line',
      image: 'https://readdy.ai/api/search-image?query=Agricultural%20professional%20walking%20through%20crop%20field%20with%20farmer%20inspecting%20plants%20golden%20hour%20lighting%20vast%20farmland%20background%20professional%20documentary%20photography%20style&width=600&height=400&seq=hww-step2&orientation=landscape',
    },
    {
      number: '03',
      title: 'Custom Plan',
      subtitle: 'Tailored to Your Needs',
      description: 'Based on what we\'ve learned, we put together a detailed plan covering scope, timing, products, and pricing. Everything is transparent—no hidden fees, no surprises.',
      details: [
        'Service recommendations',
        'Application rates and products',
        'Timeline and scheduling windows',
        'Clear, upfront pricing',
      ],
      icon: 'ri-file-list-3-line',
      image: 'https://readdy.ai/api/search-image?query=Agricultural%20planning%20documents%20and%20maps%20spread%20on%20wooden%20table%20with%20laptop%20showing%20field%20data%20professional%20business%20setting%20warm%20lighting%20farm%20office%20environment&width=600&height=400&seq=hww-step3&orientation=landscape',
    },
    {
      number: '04',
      title: 'Scheduling',
      subtitle: 'Timing That Works',
      description: 'We coordinate with your schedule and watch the weather. When the window opens, we\'re ready. You\'ll know exactly when we\'re coming and what to expect.',
      details: [
        'Flexible scheduling around your operation',
        'Weather monitoring and contingency planning',
        'Advance notification before arrival',
        'Coordination with your other activities',
      ],
      icon: 'ri-calendar-check-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20checking%20weather%20app%20and%20calendar%20on%20tablet%20device%20agricultural%20field%20in%20background%20sunrise%20morning%20light%20planning%20and%20preparation%20documentary%20style&width=600&height=400&seq=hww-step4&orientation=landscape',
    },
    {
      number: '05',
      title: 'Execution',
      subtitle: 'Precision in the Field',
      description: 'Our certified pilots arrive on time, set up efficiently, and execute the plan with precision. Safety protocols are followed to the letter. You can watch or go about your day—we\'ve got it handled.',
      details: [
        'Pre-flight safety checks',
        'GPS-guided precision application',
        'Real-time monitoring and adjustments',
        'Minimal disruption to your operation',
      ],
      icon: 'ri-plane-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20drone%20pilot%20operating%20agricultural%20spray%20drone%20over%20green%20crop%20field%20precision%20application%20technology%20safety%20vest%20professional%20equipment%20cinematic%20lighting&width=600&height=400&seq=hww-step5&orientation=landscape',
    },
    {
      number: '06',
      title: 'Documentation',
      subtitle: 'Complete Records',
      description: 'After every job, you receive detailed documentation: flight logs, application maps, coverage reports, and photos. Everything you need for your records and peace of mind.',
      details: [
        'Flight path and coverage maps',
        'Application rates and totals',
        'Before and after imagery',
        'Digital records for your files',
      ],
      icon: 'ri-folder-chart-line',
      image: 'https://readdy.ai/api/search-image?query=Digital%20tablet%20showing%20aerial%20field%20map%20with%20data%20overlay%20agricultural%20analytics%20professional%20reporting%20interface%20clean%20modern%20design%20warm%20office%20lighting&width=600&height=400&seq=hww-step6&orientation=landscape',
    },
    {
      number: '07',
      title: 'Follow-Up',
      subtitle: 'Ongoing Partnership',
      description: 'We check in after the job to make sure you\'re satisfied and discuss results. This isn\'t a one-and-done transaction—it\'s the beginning of a working relationship.',
      details: [
        'Post-service check-in call',
        'Results review and discussion',
        'Recommendations for next steps',
        'Seasonal planning for future needs',
      ],
      icon: 'ri-hand-heart-line',
      image: 'https://readdy.ai/api/search-image?query=Farmer%20shaking%20hands%20with%20agricultural%20service%20provider%20in%20field%20successful%20partnership%20trust%20satisfaction%20golden%20hour%20warm%20lighting%20professional%20documentary%20style&width=600&height=400&seq=hww-step7&orientation=landscape',
    },
  ];

  const commitments = [
    {
      icon: 'ri-time-line',
      title: 'On-Time Arrival',
      description: 'When we say we\'ll be there, we\'ll be there. Your time matters.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Safety First',
      description: 'Every flight follows strict protocols. No exceptions, no shortcuts.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Transparent Pricing',
      description: 'The price we quote is the price you pay. No hidden fees.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Direct Communication',
      description: 'You\'ll always have a direct line to your pilot and our team.',
    },
  ];

  const faqs = [
    {
      question: 'How far in advance should I schedule?',
      answer: 'For planned applications, we recommend 2-3 weeks notice. For urgent needs during peak season, 717-913-1780—we\'ll do everything we can to fit you in.',
    },
    {
      question: 'What if weather delays the job?',
      answer: 'We monitor conditions closely and will reschedule at no extra cost if weather prevents safe or effective application. We\'ll keep you informed every step of the way.',
    },
    {
      question: 'Do I need to be present during the work?',
      answer: 'Not required, but you\'re welcome to watch. We\'ll coordinate access and keep you updated via text or call throughout the job.',
    },
    {
      question: 'How quickly can I get a quote?',
      answer: 'For straightforward jobs, often within 24-48 hours of our initial conversation. Larger or more complex projects may require a site visit first.',
    },
    {
      question: 'What forms of payment do you accept?',
      answer: 'We accept checks, credit cards, and ACH transfers. Payment terms are discussed upfront and outlined in your service agreement.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title="How We Work - Our Process From Start to Finish | Greenfield Partners"
        description="From first conversation to completed project, here's exactly what to expect when you partner with Greenfield. No surprises, no runaround—just a clear path to results."
        keywords="agricultural service process, drone application process, farm service workflow, transparent pricing"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Aerial%20view%20of%20organized%20agricultural%20farmland%20with%20geometric%20patterns%20of%20different%20crop%20fields%20at%20sunrise%20golden%20light%20professional%20drone%20photography%20cinematic%20landscape%20systematic%20orderly%20farming&width=1920&height=800&seq=hww-hero-1&orientation=landscape')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <p className="text-emerald-400 font-semibold tracking-wide mb-4">OUR PROCESS</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6 max-w-3xl">
            How We Work<br />With You
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl leading-relaxed">
            From first conversation to completed project, here's exactly what to expect when you partner with Greenfield. No surprises, no runaround—just a clear path to results.
          </p>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Intro Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            A Process Built on Trust
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            We know you've got enough uncertainty in farming without adding more from your service providers. That's why we've built a process that's straightforward, transparent, and focused on getting you results. Every step is designed to keep you informed and in control.
          </p>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Process Steps Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-emerald-800 font-semibold tracking-wide mb-4">STEP BY STEP</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              From First Call to Final Report
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Seven clear steps that take you from initial contact to completed project—and beyond.
            </p>
          </div>

          <div className="space-y-16">
            {/* Step 1 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4">Initial Consultation</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  We start by understanding your operation, your challenges, and your goals. This isn't a sales pitch—it's a conversation about whether our services make sense for your situation.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Want to see potential savings first? <a href="/roi-calculator" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">Use our ROI calculator <i className="ri-arrow-right-line"></i></a> to estimate your return before we talk.
                </p>
                <ul className="space-y-3">
                  {processSteps[0].details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-lg text-emerald-700"></i>
                      </div>
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="aspect-[3/2] rounded-xl overflow-hidden shadow-xl">
                <img
                  src={processSteps[0].image}
                  alt={processSteps[0].title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Step 2 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-1 md:order-2">
                <h3 className="text-3xl font-bold mb-4">Field Assessment & Quote</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Before we quote, we verify. We review your fields, confirm boundaries, identify obstacles, and assess complexity factors that affect pricing.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  For standard fields, you can <a href="/instant-quote" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">get an instant quote online <i className="ri-arrow-right-line"></i></a>. Complex terrain? We'll schedule a site visit.
                </p>
                <ul className="space-y-3">
                  {processSteps[1].details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-lg text-emerald-700"></i>
                      </div>
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="aspect-[3/2] rounded-xl overflow-hidden shadow-xl">
                <img
                  src={processSteps[1].image}
                  alt={processSteps[1].title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Step 3 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4">Custom Plan</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Based on what we\'ve learned, we put together a detailed plan covering scope, timing, products, and pricing. Everything is transparent—no hidden fees, no surprises.
                </p>
                <ul className="space-y-3">
                  {processSteps[2].details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-lg text-emerald-700"></i>
                      </div>
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="aspect-[3/2] rounded-xl overflow-hidden shadow-xl">
                <img
                  src={processSteps[2].image}
                  alt={processSteps[2].title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Step 4 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4">Scheduling</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  We coordinate with your schedule and watch the weather. When the window opens, we\'re ready. You\'ll know exactly when we\'re coming and what to expect.
                </p>
                <ul className="space-y-3">
                  {processSteps[3].details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-lg text-emerald-700"></i>
                      </div>
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="aspect-[3/2] rounded-xl overflow-hidden shadow-xl">
                <img
                  src={processSteps[3].image}
                  alt={processSteps[3].title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Step 5 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4">Execution</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Our certified pilots arrive on time, set up efficiently, and execute the plan with precision. Safety protocols are followed to the letter. You can watch or go about your day—we\'ve got it handled.
                </p>
                <ul className="space-y-3">
                  {processSteps[4].details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-lg text-emerald-700"></i>
                      </div>
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="aspect-[3/2] rounded-xl overflow-hidden shadow-xl">
                <img
                  src={processSteps[4].image}
                  alt={processSteps[4].title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Step 6 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4">Documentation</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  After every job, you receive detailed documentation: flight logs, application maps, coverage reports, and photos. Everything you need for your records and peace of mind.
                </p>
                <ul className="space-y-3">
                  {processSteps[5].details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-lg text-emerald-700"></i>
                      </div>
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="aspect-[3/2] rounded-xl overflow-hidden shadow-xl">
                <img
                  src={processSteps[5].image}
                  alt={processSteps[5].title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Step 7 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4">Follow-Up</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  We check in after the job to make sure you\'re satisfied and discuss results. This isn\'t a one-and-done transaction—it\'s the beginning of a working relationship.
                </p>
                <ul className="space-y-3">
                  {processSteps[6].details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-lg text-emerald-700"></i>
                      </div>
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="aspect-[3/2] rounded-xl overflow-hidden shadow-xl">
                <img
                  src={processSteps[6].image}
                  alt={processSteps[6].title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Timeline Visual */}
      <section className="py-24 topo-cta-bg text-white relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-emerald-400 font-semibold tracking-wide mb-4">TYPICAL TIMELINE</p>
            <h2 className="text-4xl font-bold mb-6">
              What to Expect, When
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Every project is different, but here's a general sense of how things move from start to finish.
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-emerald-700/30 -translate-y-1/2"></div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
              {[
                { day: 'Day 1', title: 'Initial Call', desc: 'We talk, we listen, we learn about your needs' },
                { day: 'Day 2-5', title: 'Site Visit', desc: 'We come out to see your land firsthand' },
                { day: 'Day 5-7', title: 'Proposal', desc: 'You receive a detailed plan and quote' },
                { day: 'Scheduled', title: 'Execution', desc: 'We complete the work on the agreed date' },
                { day: 'Within 48hrs', title: 'Documentation', desc: 'Full reports delivered to you' },
              ].map((item, index) => (
                <div key={index} className="relative text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-700 rounded-full mx-auto mb-4 relative z-10">
                    <span className="text-lg font-bold">{index + 1}</span>
                  </div>
                  <p className="text-emerald-400 font-semibold mb-2">{item.day}</p>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* Our Commitments */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-emerald-800 font-semibold tracking-wide mb-4">OUR PROMISE</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What You Can Count On
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These aren't aspirations—they're commitments we make to every client, every time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {commitments.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 text-center shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-6">
                  <i className={`${item.icon} text-3xl text-emerald-800`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-emerald-800 font-semibold tracking-wide mb-4">COMMON QUESTIONS</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Frequently Asked
            </h2>
            <p className="text-lg text-gray-600">
              Answers to the questions we hear most often about working with us.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  <i className="ri-add-line text-xl text-emerald-700 group-open:hidden"></i>
                  <i className="ri-subtract-line text-xl text-emerald-700 hidden group-open:block"></i>
                </summary>
                <div className="px-6 pb-6 pt-0">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}

            <details className="group bg-white rounded-xl shadow-sm overflow-hidden">
              <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                <h3 className="font-semibold text-gray-900 pr-4">How do I know if an operator is qualified?</h3>
                <i className="ri-add-line text-xl text-emerald-700 group-open:hidden"></i>
                <i className="ri-subtract-line text-xl text-emerald-700 hidden group-open:block"></i>
              </summary>
              <div className="px-6 pb-6 pt-0">
                <p className="text-gray-600 leading-relaxed">
                  All our pilots hold FAA Part 107 certification, carry full liability insurance, and have specialized agricultural training. Want to evaluate any drone operator? Use our <a href="/operator-checklist" className="text-emerald-800 hover:text-emerald-700 font-semibold">Operator Checklist tool</a> to assess credentials and experience before hiring.
                </p>
              </div>
            </details>

            <details className="group bg-white rounded-xl shadow-sm overflow-hidden">
              <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                <h3 className="font-semibold text-gray-900 pr-4">Do you offer seasonal programs?</h3>
                <i className="ri-add-line text-xl text-emerald-700 group-open:hidden"></i>
                <i className="ri-subtract-line text-xl text-emerald-700 hidden group-open:block"></i>
              </summary>
              <div className="px-6 pb-6 pt-0">
                <p className="text-gray-600 leading-relaxed">
                  Yes. Our <a href="/seasonal-programs" className="text-emerald-800 hover:text-emerald-700 font-semibold">seasonal application programs</a> offer locked pricing, priority scheduling, and guaranteed execution windows. Most farmers find that planning ahead reduces stress during critical spray windows and saves money through volume pricing.
                </p>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Topo Divider */}
      <div className="topo-divider"></div>

      {/* CTA Section */}
      <section className="py-24 topo-cta-bg text-white relative">
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-emerald-700 rounded-full mx-auto mb-8">
            <i className="ri-rocket-line text-4xl text-white"></i>
          </div>
          <h2 className="text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-emerald-100 leading-relaxed mb-8 max-w-2xl mx-auto">
            The first step is simple: let's talk. Tell us about your operation, your challenges, and what you're hoping to accomplish. No pressure, no obligation—just a conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#contact"
              className="inline-block bg-white text-emerald-800 hover:bg-emerald-50 font-bold px-10 py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-lg shadow-lg"
            >
              Start a Conversation
            </a>
            <a
              href="tel:7179131780"
              className="inline-flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-600 text-white font-bold px-10 py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-lg border-2 border-emerald-600"
            >
              <i className="ri-phone-line text-xl"></i>
              Call Us Direct
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
